
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import BackButton from "./BackButton";
import { fetchProductDetail } from "../api/productDetailApi";
import { getUserProfile, getUsername } from "../utils/auth";
import { useCart } from "../context/CartContext";
import { userDetail } from "../HardcodeValues/userdetail";
import {
  isUsableProductDetail,
  toolResultToProductDetail,
} from "../utils/productDetailFromToolResult";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import WishlistToggle from "./WishlistToggle";
import TryOnStage from "./TryOnStage";
import morphLayout from "../utils/morphLayout";
import { registerInlineTryOn } from "../utils/inlineTryOn";
import { trackProductViewed } from "../services/uiAgentSync";
import { getColorHex } from "../utils/colorHex";
import PriceMatchCelebration from "./PriceMatchCelebration";
import {
  getPriceMatch,
  subscribePriceMatch,
  takePriceMatchCelebration,
} from "../utils/priceMatch";

// Extract color from variant (API may not have attributes.color - get from categories or title)
function normalizeColorLabel(color) {
  if (color == null || color === "") return null;
  if (Array.isArray(color)) {
    const first = color.find((c) => c != null && String(c).trim() !== "");
    return first != null ? String(first).trim() : null;
  }
  if (typeof color === "string") {
    const trimmed = color.trim();
    return trimmed || null;
  }
  const asString = String(color).trim();
  return asString || null;
}

function getVariantColor(variant) {
  if (variant?.attributes?.color) {
    const normalized = normalizeColorLabel(variant.attributes.color);
    if (normalized) return normalized;
  }
  const cat = variant?.categories?.[0];
  if (cat) {
    const parts = cat.split(" > ").map((p) => p.trim()).filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && !["Dark", "Light"].includes(last)) return last;
    if (parts.length >= 2) return parts[parts.length - 2];
  }
  const title = variant?.title || "";
  const match = title.match(/(?:Titti\s+)?(Dark\s+\w+|\w+)\s+(?:Jersey|Melange|Solid)/i) ||
    title.match(/(Black|Blue|Grey|Gray|Red|White|Navy|Brown|Beige|Green|Pink|Yellow)/i);
  return match ? match[1].trim() : null;
}

export default function HomeProductDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: productId } = useParams();
  const { cartItems, addToCart, updateQuantity } = useCart();

  const [productDetail, setProductDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Price match: the record may already exist (the agent matched, then brought
  // us here) or arrive while this page is open. Either way the celebration is
  // claimed exactly once, by `takePriceMatchCelebration`.
  const [priceMatch, setPriceMatch] = useState(() => getPriceMatch(productId));
  const [celebrating, setCelebrating] = useState(false);

  useEffect(() => {
    setPriceMatch(getPriceMatch(productId));
    setCelebrating(Boolean(takePriceMatchCelebration(productId)));

    return subscribePriceMatch((entry) => {
      if (!entry || String(entry.productId) !== String(productId)) return;
      setPriceMatch(getPriceMatch(productId));
      if (takePriceMatchCelebration(productId)) setCelebrating(true);
    });
  }, [productId]);

  // UI → Agent sync: last product id reported via product_viewed
  const trackedProductIdRef = useRef(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState(null); // null = main product
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewQuantity, setPreviewQuantity] = useState(1);

  // Virtual Try-On state
  const [tryOnOpen, setTryOnOpen] = useState(false);
  // Inline try-on: `autoStart` is the agent's mode; `tryOnSession` remounts
  // the mirror so a second agent request starts a fresh render.
  const [tryOnAutoStart, setTryOnAutoStart] = useState(null);
  const [tryOnSession, setTryOnSession] = useState(0);
  // Opening and closing re-lay the page (photo and mirror side by side,
  // details below), so both run as an animated morph.
  const openTryOn = useCallback((autoStart = null) => {
    morphLayout(() => {
      setTryOnAutoStart(autoStart);
      setTryOnSession((n) => n + 1);
      setTryOnOpen(true);
    });
  }, []);
  const closeTryOn = useCallback(() => {
    morphLayout(() => setTryOnOpen(false));
  }, []);
  // Let an agent-started try-on for this product open here, inline, instead
  // of in the fallback popup.
  useEffect(
    () =>
      registerInlineTryOn(productId, {
        open: ({ autoStart }) => openTryOn(autoStart),
        close: closeTryOn,
      }),
    [productId, openTryOn, closeTryOn]
  );

  const userProfile = getUserProfile();
  const customerId = userProfile?.customer_id;
  const username = getUsername();

  // Collect all images from product and variants
  const allImages = useMemo(() => {
    const images = [];

    if (productDetail?.product?.images) {
      productDetail.product.images.forEach((img) => {
        images.push({
          url: img,
          id: productDetail.product.id,
          label: "Main",
        });
      });
    }

    if (productDetail?.variants) {
      productDetail.variants.forEach((variant) => {
        if (variant.images) {
          variant.images.forEach((img) => {
            images.push({
              url: img,
              id: variant.id,
              label: variant.attributes?.color || "Variant",
            });
          });
        }
      });
    }

    return images;
  }, [productDetail]);

  // Get colors from variants (with variant ref for selection)
  const colorListWithVariants = useMemo(() => {
    const list = [];
    const product = productDetail?.product;
    const variants = productDetail?.variants || [];

    // Add main product color
    const productColor = product && getVariantColor(product);
    if (productColor) {
      list.push({ color: productColor, variantId: null });
    }
    // Add variant colors (avoid duplicates)
    variants.forEach((variant) => {
      const color = getVariantColor(variant);
      if (color && !list.some((x) => x.color === color)) {
        list.push({ color, variantId: variant.id });
      }
    });
    // Fallback: attributes.color if getVariantColor didn't find any
    if (list.length === 0 && product?.attributes?.color) {
      const raw = product.attributes.color;
      const colors = Array.isArray(raw)
        ? raw.map(normalizeColorLabel).filter(Boolean)
        : String(raw)
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
      colors.forEach((c) => list.push({ color: c, variantId: null }));
    }
    return list;
  }, [productDetail]);

  const colorList = useMemo(
    () => colorListWithVariants.map((x) => x.color),
    [colorListWithVariants]
  );

  // Safely get current display image (handles out-of-bounds index when switching products)
  const currentDisplayImage = useMemo(() => {
    if (!allImages.length) return null;
    const idx = Math.min(currentImageIndex, allImages.length - 1);
    return allImages[idx] || null;
  }, [allImages, currentImageIndex]);

  // Display product: selected variant or main product
  const displayProduct = useMemo(() => {
    if (!productDetail?.product) return null;
    if (selectedVariantId && productDetail.variants?.length) {
      const variant = productDetail.variants.find((v) => v.id === selectedVariantId);
      if (variant) return variant;
    }
    return productDetail.product;
  }, [productDetail, selectedVariantId]);

  // UI → Agent sync: report the viewed product — fires on initial load and
  // again whenever the shopper switches variants (thumbnail or color click),
  // so the agent always knows which variant is on screen.
  useEffect(() => {
    if (!displayProduct?.id) return;
    const id = String(displayProduct.id);
    if (trackedProductIdRef.current === id) return;
    const isInPageSwitch = trackedProductIdRef.current !== null;
    trackedProductIdRef.current = id;
    trackProductViewed({
      productId: id,
      productTitle: displayProduct.title ?? displayProduct.name ?? null,
      fromView: isInPageSwitch ? "product_detail" : null,
    });
  }, [displayProduct]);

  // Find existing cart item matching id (variant or main), size, and color
  const displayProductId = displayProduct?.id || productId;
  const existingCartItem = useMemo(() => {
    if (!displayProductId || !selectedSize) return null;
    return cartItems.find(
      (item) =>
        item.id === displayProductId &&
        item.size === selectedSize &&
        item.color === selectedColor
    );
  }, [cartItems, displayProductId, selectedSize, selectedColor]);

  const isInCart = !!existingCartItem;
  const quantity = isInCart ? existingCartItem.quantity : previewQuantity;
  const isAlreadyInCart = isInCart;

  const applyProductDetailData = useCallback(
    (data) => {
      setProductDetail(data);
      setSelectedVariantId(null);
      setCurrentImageIndex(0);

      const firstColor =
        getVariantColor(data?.product) ||
        getVariantColor(data?.variants?.[0]) ||
        normalizeColorLabel(data?.product?.attributes?.color);
      if (firstColor) setSelectedColor(firstColor);

      if (data?.product?.attributes?.size) {
        const sizes = Array.isArray(data.product.attributes.size)
          ? data.product.attributes.size
          : String(data.product.attributes.size)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);

        if (sizes.length > 0) {
          const productCategories = data?.product?.categories || [];
          const categoryString = Array.isArray(productCategories)
            ? productCategories.join(" ").toLowerCase()
            : String(productCategories).toLowerCase();
          const isFootwear =
            categoryString.includes("footwear") ||
            categoryString.includes("shoe") ||
            categoryString.includes("sandal") ||
            categoryString.includes("boot");

          let defaultSize = null;
          if (username && userDetail[username]) {
            const userSizes = userDetail[username];
            defaultSize = isFootwear
              ? userSizes["Footwear Size"]
              : userSizes["Size"];
          }

          if (defaultSize && sizes.includes(defaultSize)) {
            setSelectedSize(defaultSize);
          } else {
            setSelectedSize(sizes[0]);
          }
        }
      }

    },
    [username]
  );

  const resolvePreloadedProductDetail = useCallback(() => {
    const navState = location.state;
    if (!navState || typeof navState !== "object") return null;

    if (isUsableProductDetail(navState.productDetail, productId)) {
      return navState.productDetail;
    }

    if (navState.product) {
      const fromProduct = toolResultToProductDetail(navState.product);
      if (isUsableProductDetail(fromProduct, productId)) {
        return fromProduct;
      }
    }

    return null;
  }, [location.state, productId]);

  useEffect(() => {
    if (!productId) {
      setError("Missing product ID");
      setLoading(false);
      return;
    }

    if (!customerId) {
      sessionStorage.setItem("pendingProductId", productId);
      window.dispatchEvent(
        new CustomEvent("openLoginModal", { detail: { productId } })
      );
      setError("Please sign in to view product details");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const preloaded = resolvePreloadedProductDetail();
        if (preloaded) {
          if (!cancelled) {
            applyProductDetailData(preloaded);
          }
          return;
        }

        const data = await fetchProductDetail(productId, customerId);
        if (!cancelled) {
          applyProductDetailData(data);
        }
      } catch (err) {
        console.error("Failed to fetch product detail:", err);
        if (!cancelled) {
          setError(err.message || "Failed to load product details");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [
    productId,
    customerId,
    location.key,
    applyProductDetailData,
    resolvePreloadedProductDetail,
  ]);

  const cleanImageUrl = (url) => {
    if (typeof url !== "string") return "";
    return toPublicStorageUrl(url.trim());
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert("Please select a size before adding to cart.");
      return;
    }

    if (!productDetail?.product) {
      alert("Product data is missing.");
      return;
    }

    const product = productDetail.product;

    if (existingCartItem) {
      if (quantity !== existingCartItem.quantity) {
        updateQuantity(existingCartItem.id, quantity);
      }
      return;
    }

    addToCart({
      id: product?.id || productId,
      title: product.title || "Product",
      price: parseFloat(
        product.price_info?.price || product.price_info?.originalPrice || 0
      ),
      currency:
        product.price_info?.currencyCode ||
        product.price_info?.currency ||
        "USD",
      description: product.description || "",
      image: product.images?.[0] ? cleanImageUrl(product.images[0]) : "",
      size: selectedSize,
      color: selectedColor,
      variants: productDetail.variants || [],
      quantity: previewQuantity,
    });
  };

  // Handle thumbnail click: update image and variant selection
  const handleThumbnailClick = (index, image) => {
    setCurrentImageIndex(index);
    const product = productDetail?.product;
    const variants = productDetail?.variants || [];
    if (image?.id && product?.id !== image.id) {
      const variant = variants.find((v) => v.id === image.id);
      if (variant) {
        setSelectedVariantId(variant.id);
        const color = getVariantColor(variant);
        if (color) setSelectedColor(color);
      }
    } else {
      setSelectedVariantId(null);
      if (product) {
        const color = getVariantColor(product);
        if (color) setSelectedColor(color);
      }
    }
  };

  // Handle color selection: switch to that variant's data and image
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    const entry = colorListWithVariants.find((x) => x.color === color);
    if (entry) {
      setSelectedVariantId(entry.variantId);
      // Switch to that variant's image in the gallery
      if (entry.variantId) {
        const idx = allImages.findIndex((img) => img.id === entry.variantId);
        if (idx >= 0) setCurrentImageIndex(idx);
      } else {
        // Main product - switch to first product image
        const idx = allImages.findIndex((img) => img.id === productDetail?.product?.id);
        if (idx >= 0) setCurrentImageIndex(idx);
      }
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const incrementQuantity = () => {
    if (isInCart) {
      updateQuantity(existingCartItem.id, existingCartItem.quantity + 1);
    } else {
      setPreviewQuantity((prev) => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (isInCart) {
      if (existingCartItem.quantity > 1) {
        updateQuantity(existingCartItem.id, existingCartItem.quantity - 1);
      } else {
        updateQuantity(existingCartItem.id, 0);
      }
    } else if (previewQuantity > 1) {
      setPreviewQuantity((prev) => prev - 1);
    }
  };

  // Handle size change
  const handleSizeChange = (newSize) => {
    setSelectedSize(newSize);
    
  };

  if (loading) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8">
        <BackButton />
        <div className="grid md:grid-cols-2 gap-7 lg:gap-12 mt-4">
          <div
            className="ob-skel"
            style={{ aspectRatio: "3 / 4", borderRadius: "var(--ob-r-lg)" }}
          />
          <div className="md:pt-2">
            <div className="ob-skel h-3 w-24" />
            <div className="ob-skel h-9 w-full mt-3" />
            <div className="ob-skel h-9 w-2/3 mt-2" />
            <div className="ob-skel h-7 w-32 mt-6" />
            <div className="ob-skel h-3 w-full mt-6" />
            <div className="ob-skel h-3 w-5/6 mt-2" />
            <div className="ob-skel h-11 w-full mt-8" style={{ borderRadius: 14 }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || (!productDetail?.product && !loading)) {
    const isAuthError = error === "Please sign in to view product details";

    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
        <BackButton />
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-6 h-6"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.2v.1" />
            </svg>
          </div>
          <div
            className="ob-display text-[26px]"
            style={{ color: "var(--ob-ink)" }}
          >
            {error || "Product data not available"}
          </div>
          {isAuthError && (
            <p className="text-[13px] mt-2 max-w-[380px] mx-auto leading-relaxed">
              The sign-in dialog should open automatically. If it does not, use
              the account button in the top bar.
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ob-btn ob-btn-solid ob-btn-lg mt-6"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const product = displayProduct || productDetail?.product;
  const originalPrice = parseFloat(product?.price_info?.originalPrice) || 0;
  const displayPrice = parseFloat(product?.price_info?.price) || originalPrice;
  const isOnSale = originalPrice > displayPrice && displayPrice > 0;

  const sizeList = product?.attributes?.size
    ? Array.isArray(product?.attributes?.size)
      ? product.attributes.size
      : String(product?.attributes?.size)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
    : [];

  const rating = parseFloat(product.attributes?.rating) || 4.9;
  const reviewCount = parseInt(product.attributes?.review_count) || 89;

  const sizeFitNote =
    product?.attributes?.size_fit_note ||
    product?.size_fit_note ||
    productDetail?.product?.size_fit_note ||
    productDetail?.size_fit_note;

  const offPercent = isOnSale
    ? Math.round((1 - displayPrice / originalPrice) * 100)
    : 0;

  // A match only counts while it actually beats the shelf price — a stale or
  // equal record must never draw a strike-through over nothing.
  const matchedPrice =
    priceMatch && priceMatch.matchedPrice < displayPrice
      ? priceMatch.matchedPrice
      : null;
  const heroPrice = matchedPrice ?? displayPrice;
  // What the number counts down from, and what sits struck through beside it.
  const wasPrice =
    matchedPrice != null
      ? priceMatch.originalPrice ?? displayPrice
      : isOnSale
        ? originalPrice
        : null;
  const savedAmount = matchedPrice != null && wasPrice ? wasPrice - matchedPrice : 0;

  // Derived from the id, not Math.random(): the old inline random re-rolled
  // on every render, so the number flickered whenever anything else changed.
  const stockLeft =
    (String(product.id || "0")
      .split("")
      .reduce((sum, ch) => sum + ch.charCodeAt(0), 0) %
      9) +
    1;

  const specs = [
    { label: "SKU", value: product.id || "N/A" },
    {
      label: "Category",
      value: product.categories?.[0]?.split(" > ").pop() || "N/A",
    },
    { label: "Material", value: product.attributes?.fabric || "N/A" },
    { label: "Fit", value: product.attributes?.fit || "N/A" },
  ];

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8 ob-viewin">
      <BackButton />

      <div
        className={`ob-pd-grid grid gap-7 lg:gap-12 mt-3 ${
          tryOnOpen ? "ob-pd-staged" : "md:grid-cols-2"
        }`}
      >
        {/* ══ Gallery — or, while trying on, the mirror in its place ══ */}
        {tryOnOpen ? (
          <TryOnStage
            key={tryOnSession}
            productId={productId}
            productTitle={product.title}
            image={currentDisplayImage ? cleanImageUrl(currentDisplayImage.url) : ""}
            count={allImages.length}
            index={currentImageIndex}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
            autoStart={tryOnAutoStart}
            onExit={closeTryOn}
          />
        ) : (
        <div className="flex gap-3">
          {/* Thumbnail rail — a column on desktop, a row under the hero on
              phones, where vertical space is the scarce axis. */}
          {allImages.length > 1 && (
            <div className="ob-thumbrail ob-scroll">
              {allImages.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  className={`ob-thumb ${currentImageIndex === index ? "ob-sel" : ""}`}
                  onClick={() => handleThumbnailClick(index, image)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={cleanImageUrl(image.url)}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = placeholderImage(`thumb${index}`, 100, 120);
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div
              className="ob-hero"
              onClick={() => {
                openTryOn();
              }}
            >
              <img
                src={
                  currentDisplayImage ? cleanImageUrl(currentDisplayImage.url) : ""
                }
                alt={product.title}
                decoding="async"
                onError={(e) => {
                  const seed = (productId || "fallback").replace(/\s+/g, "");
                  e.target.src = placeholderImage(seed, 800, 1066);
                }}
              />

              {allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                    aria-label="Previous image"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-[17px] h-[17px]"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="ob-heronav ob-heronav-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    aria-label="Next image"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-[17px] h-[17px]"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </>
              )}

              <span
                className="ob-tagline"
                onClick={(e) => e.stopPropagation()}
                style={{ pointerEvents: "none" }}
              >
                Highly rated
              </span>

              {/* The try-on entry point. The whole hero is clickable too, but
                  a hero that silently opens a modal needs a visible cue. */}
              <button
                type="button"
                className="ob-tryonchip"
                onClick={(e) => {
                  e.stopPropagation();
                  openTryOn();
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[14px] h-[14px]"
                >
                  <rect x="5" y="2" width="14" height="17" rx="7" />
                  <path d="M9 22h6M12 19v3" />
                  <path d="M10 9a2 2 0 0 1 4 0" />
                </svg>
                Try it on
              </button>

              {allImages.length > 1 && (
                <div className="ob-herodots" onClick={(e) => e.stopPropagation()}>
                  {allImages.map((_, i) => (
                    <span
                      key={i}
                      className={`ob-herodot ${i === currentImageIndex ? "ob-on" : ""}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* ══ Info ══ — beside the gallery normally; below the try-on stage,
            as story (left) and buying (right), in try-on mode ══ */}
        <div className="md:pt-1 ob-pd-info">
          <div className="ob-pd-intro">
          {product.categories?.[0] && (
            <div className="ob-card-brand">
              {product.categories[0].split(" > ")[0]}
            </div>
          )}

          <h1 className="ob-display text-[clamp(1.75rem,4vw,2.4rem)] leading-[1.08] mt-1.5">
            {product.title}
          </h1>

          <div className="flex items-center gap-1.5 mt-2.5">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-3.5 h-3.5"
              style={{ color: "var(--ob-warn)" }}
            >
              <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9Z" />
            </svg>
            <span className="text-[12.5px] font-semibold ob-ink">
              {rating}
            </span>
            <span className="text-[12px] ob-ink3">
              ({reviewCount} reviews)
            </span>
          </div>

          <div className="flex items-baseline gap-3 flex-wrap mt-4">
            <span
              className={`ob-pricecell text-[27px] font-bold ob-tabnum ${
                celebrating ? "ob-celebrating" : ""
              }`}
              style={{
                color:
                  matchedPrice != null
                    ? "var(--ob-ok)"
                    : isOnSale
                      ? "var(--ob-err)"
                      : "var(--ob-ink)",
              }}
            >
              {celebrating ? (
                <PriceMatchCelebration
                  from={wasPrice}
                  to={heroPrice}
                  onDone={() => setCelebrating(false)}
                />
              ) : (
                `$${heroPrice.toFixed(2)}`
              )}
            </span>

            {wasPrice != null && wasPrice > heroPrice && (
              <span className="text-[15px] ob-ink3 line-through ob-tabnum">
                ${wasPrice.toFixed(2)}
              </span>
            )}

            {matchedPrice != null ? (
              <span
                className={`ob-pricematch ${celebrating ? "ob-fresh" : ""}`}
                role="status"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.1}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[12px] h-[12px]"
                  aria-hidden="true"
                >
                  <path d="m4 12.5 5 5L20 6.5" />
                </svg>
                Price matched
                {savedAmount > 0 ? ` · you save $${savedAmount.toFixed(2)}` : ""}
              </span>
            ) : (
              isOnSale && (
                <span className="ob-chip ob-chip-err">{offPercent}% off</span>
              )
            )}
          </div>

          {matchedPrice != null && priceMatch?.competitor && (
            <div className="ob-pricematch-by">
              Matched against {priceMatch.competitor}. Applied automatically at
              checkout.
            </div>
          )}

          {product.description && (
            <p className="text-[13.5px] ob-ink2 leading-relaxed mt-4">
              {product.description}
            </p>
          )}

          </div>

          <div className="ob-pd-buy">
          {/* Colour */}
          {colorList.length > 0 && (
            <div className="mt-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="ob-lbl mb-0">Colour</span>
                <span className="text-[11.5px] ob-ink2 font-medium">
                  {selectedColor}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {colorList.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ob-swatch ${selectedColor === color ? "ob-sel" : ""}`}
                    style={{ "--ob-swatch": getColorHex(color) }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                    aria-label={`Colour ${color}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {sizeList.length > 0 && (
            <div className="mt-6">
              <div className="flex items-baseline justify-between mb-2">
                <span className="ob-lbl mb-0">Size</span>
                {!selectedSize && (
                  <span
                    className="text-[10.5px] font-semibold"
                    style={{ color: "var(--ob-warn)" }}
                  >
                    pick one, or Orbiee will ask
                  </span>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {sizeList.map((size, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ob-size ${selectedSize === size ? "ob-sel" : ""}`}
                    onClick={() => handleSizeChange(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fit note */}
          {sizeFitNote && (
            <div className="ob-fitnote mt-4">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[15px] h-[15px] shrink-0 mt-[2px]"
              >
                <path d="M3 7h18M3 12h18M3 17h10" />
              </svg>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[.1em] mb-1">
                  Fit note
                </div>
                <p className="text-[12.5px] leading-relaxed whitespace-pre-line">
                  {sizeFitNote}
                </p>
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-6">
            <span className="ob-lbl">Quantity</span>
            <div className="flex items-center gap-3">
              <span className="ob-qty">
                <button
                  type="button"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <RemoveIcon sx={{ fontSize: 15 }} />
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={incrementQuantity}
                  aria-label="Increase quantity"
                >
                  <AddIcon sx={{ fontSize: 15 }} />
                </button>
              </span>
              <span className="text-[11.5px] ob-ink3">
                Only {stockLeft} left in stock
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-6">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!selectedSize}
              className={`ob-btn ob-btn-lg flex-1 ${
                isAlreadyInCart ? "ob-btn-ok" : "ob-btn-solid"
              }`}
            >
              {isAlreadyInCart ? (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-[15px] h-[15px]"
                  >
                    <path d="m4 12.5 5 5L20 6.5" />
                  </svg>
                  In your bag
                </>
              ) : (
                <>
                  <ShoppingCartIcon sx={{ fontSize: 16 }} />
                  {selectedSize ? "Add to bag" : "Select a size"}
                </>
              )}
            </button>

            <div className="ob-actionslot">
              <WishlistToggle product={product} />
            </div>

            <button
              type="button"
              className="ob-btn ob-btn-ghost ob-btn-lg"
              aria-label="Share"
              title="Share"
            >
              <ShareIcon sx={{ fontSize: 16 }} />
            </button>
          </div>

          <button
            type="button"
            className="ob-btn ob-btn-quiet ob-btn-sm ob-btn-block mt-2 ob-ink3 ob-pd-tryon-cta"
            onClick={() => openTryOn()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[14px] h-[14px]"
            >
              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
            </svg>
            See it on you with virtual try-on
          </button>

          {/* Reassurance */}
          <div className="grid grid-cols-3 gap-2 mt-6 pt-5 ob-hair-t">
            {[
              { Icon: LocalShippingIcon, label: "Free shipping" },
              { Icon: SecurityIcon, label: "Secure payment" },
              { Icon: RefreshIcon, label: "Easy returns" },
            ].map(({ Icon, label }) => (
              <div key={label} className="text-center">
                <Icon sx={{ fontSize: 18, color: "var(--ob-ink-3)" }} />
                <div className="text-[10.5px] ob-ink2 font-medium mt-1">
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* Specs */}
          <div className="ob-panel mt-5 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {specs.map(({ label, value }) => (
                <div key={label}>
                  <div className="ob-lbl mb-0.5">{label}</div>
                  <div className="text-[12.5px] font-medium ob-ink break-words">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </div>

    </div>
  );
}
